import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import timeZonesEnam from '@/constant/timeZonesEnam'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

/** API: UTC-0 naive, без Z/offset */
const UTC_DATE_TIME_SECONDS = 'YYYY-MM-DD HH:mm:ss'
const UTC_DATE_TIME_MINUTES = 'YYYY-MM-DD HH:mm'
const UTC_DATE = 'YYYY-MM-DD'

/** UI / DatePicker display */
const UI_DATE_TIME = 'DD.MM.YYYY HH:mm'
const UI_DATE = 'DD.MM.YYYY'

const resolvePlatformTimeZone = (timeZone?: string | null) =>
  timeZone?.trim() || timeZonesEnam.GMT_MINUS_3

const hasExplicitTimezone = (value: string) => {
  const trimmed = value.trim()
  return /[zZ]\d*(\.\d+)?$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)
}

/**
 * Парсит значение datetime-стикера (в т.ч. task_created_at / task_completed_at) как UTC.
 * Контракт бэка: строка без offset/`Z` = UTC-0.
 */
export const parseTaskTrackerSystemDatetimeUtc = (
  value: string | null | undefined,
): Dayjs | null => {
  if (value == null) return null

  const trimmed = String(value).trim()
  if (!trimmed) return null

  if (hasExplicitTimezone(trimmed)) {
    const parsed = dayjs(trimmed)
    return parsed.isValid() ? parsed.utc() : null
  }

  const strictFormats = [
    UTC_DATE_TIME_SECONDS,
    UTC_DATE_TIME_MINUTES,
    UTC_DATE,
  ]

  for (const format of strictFormats) {
    const parsed = dayjs.utc(trimmed, format, true)
    if (parsed.isValid()) return parsed
  }

  const loose = dayjs.utc(trimmed)
  return loose.isValid() ? loose : null
}

/** Показ в timezone платформы: `DD.MM.YYYY HH:mm` или emptyValue. */
export const formatTaskTrackerSystemDatetimeForDisplay = (
  value: string | null | undefined,
  platformTimeZone?: string | null,
  emptyValue = '—',
): string => {
  const utcDate = parseTaskTrackerSystemDatetimeUtc(value)
  if (!utcDate) return emptyValue

  return utcDate
    .tz(resolvePlatformTimeZone(platformTimeZone))
    .format(UI_DATE_TIME)
}

/**
 * UTC API → Dayjs для DatePicker (стенные часы platform TZ, без tz-плагина в value).
 */
export const parseTaskTrackerSystemDatetimeFilterValue = (
  value: string | null | undefined,
  platformTimeZone?: string | null,
  withTime = true,
): Dayjs | null => {
  const utcDate = parseTaskTrackerSystemDatetimeUtc(value)
  if (!utcDate) return null

  const inPlatform = utcDate.tz(resolvePlatformTimeZone(platformTimeZone))
  const displayFormat = withTime ? UI_DATE_TIME : UI_DATE
  const wall = dayjs(inPlatform.format(displayFormat), displayFormat, true)

  return wall.isValid() ? wall : null
}

/**
 * Строка из DatePicker (platform TZ wall-clock) → UTC для API / sticker_filters.
 * Формат: `YYYY-MM-DD` | `YYYY-MM-DD HH:mm` | `YYYY-MM-DD HH:mm:ss`.
 */
export const formatTaskTrackerSystemDatetimeFilterForApi = (
  displayValue: string | null | undefined,
  platformTimeZone?: string | null,
  withTime = true,
): string | null => {
  if (displayValue == null) return null

  const trimmed = String(displayValue).trim()
  if (!trimmed) return null

  const tz = resolvePlatformTimeZone(platformTimeZone)
  const displayFormat = withTime ? UI_DATE_TIME : UI_DATE
  const wall = dayjs(trimmed, displayFormat, true)
  if (!wall.isValid()) return null

  const platformNaiveFormat = withTime ? 'YYYY-MM-DD HH:mm' : UTC_DATE
  const asPlatform = dayjs.tz(
    wall.format(platformNaiveFormat),
    platformNaiveFormat,
    tz,
  )
  if (!asPlatform.isValid()) return null

  return asPlatform
    .utc()
    .format(withTime ? UTC_DATE_TIME_MINUTES : UTC_DATE)
}

/** Persist value стикера datetime: UTC `YYYY-MM-DD HH:mm:ss`. */
export const formatTaskTrackerUtcPlatformDatetimeForPersist = (
  displayValue: string | null | undefined,
  platformTimeZone?: string | null,
): string | null => {
  const minutes = formatTaskTrackerSystemDatetimeFilterForApi(
    displayValue,
    platformTimeZone,
    true,
  )
  if (minutes == null) return null

  return `${minutes}:00`
}
